'use client'

import * as React from 'react'
import { cn } from '../../helpers/index.js'
import Picture from '../../components/picture.js'
import Badge from '../../components/badge.js'
import type { ProductTag } from './types.js'

export interface ProductImageProps extends React.HTMLAttributes<HTMLDivElement> {
  /** 响应式图片集合 */
  images: any
  /** 产品名称，用作 alt 文本 */
  name: string
  /** 产品标签 */
  tags?: ProductTag[]
  /** 布局模式 */
  layout?: 'horizontal' | 'vertical'
  /** 点击回调 */
  onClick?: () => void
}

/**
 * ProductImage - 产品图片区域
 */
const ProductImage = React.forwardRef<HTMLDivElement, ProductImageProps>(
  ({ images, name, tags, layout = 'horizontal', className, onClick }, ref) => {
    const { laptop, mobile, tablet, desktop, lgDesktop } = images

    return (
      <div
        ref={ref}
        className={cn(
          'relative grow overflow-hidden',
          // 根据布局模式调整尺寸和圆角
          layout === 'horizontal'
            ? [
                // 水平布局：使用 flex-basis 设置宽度比例
                'min-h-[240px]',
                'laptop:basis-[60.9%]', // 561px / 921px ≈ 60.9%
                'desktop:basis-[57.9%]', // 760px / 1312px ≈ 57.9%
                'lg-desktop:basis-[57.9%]', // 964px / 1664px ≈ 57.9%
              ]
            : [
                // 垂直布局：上面图片，下面内容
                'laptop:h-[266px] lg-desktop:h-[364px] h-[240px] grow-0',
              ],
          onClick ? 'cursor-pointer' : '',
          className
        )}
        onClick={onClick}
      >
        <Picture
          source={`${mobile?.url}, ${tablet?.url} 768, ${laptop?.url} 1024, ${desktop?.url} 1440, ${lgDesktop?.url} 1920`}
          alt={name}
          className="absolute inset-0 size-full"
          imgClassName="size-full object-cover"
        />

        {/* 左上角标签 */}
        {tags && tags.length > 0 && (
          <div className="absolute left-4 top-4 flex gap-2">
            {tags.map((tag, index) => (
              <Badge
                key={index}
                variant={tag.variant}
                size="lg"
                promotionalType={tag.variant === 'promotional' ? tag.promotionalType : undefined}
              >
                {tag.label}
              </Badge>
            ))}
          </div>
        )}
      </div>
    )
  }
)

ProductImage.displayName = 'ProductImage'

export default ProductImage
